import {
    Box,
    Combobox,
    ComboboxGroup,
    ComboboxOption,
    ComboboxProps,
    OutlineButton,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Typography,
    IconButton,
    MenuItemProps,
    ExpansionPanel,
    ExpansionPanelSummary,
    ExpansionPanelDetails,
} from '@bb-ui/react-library';
import React, { useEffect, useMemo, useState } from 'react';
import { Trash, Information } from '@bb-ui/icons/dist/small';
import { Check } from '@bb-ui/icons/dist/medium';
import { useTranslation } from 'react-i18next';
import { camelCase } from 'lodash';
import { Link } from '@bb-ui/react-library/dist/components/Link';
import { useAppConfigContext } from '../../../../../contexts/AppConfigProvider';
import { useStyles } from './UserRoles.styles';
import { EditUserError } from '../EditUserError/EditUserError';
import { useHelpLinks } from '../../../../../hooks/useHelpLinks';

interface UserRolesProps {
    userId: string;
    initialQsGroups: string[];
    fixedQsGroups?: string[];
    disabledDisplayText?: string;
    disabledInfoText?: string;
    onRolesChange: (selectedRoles: string[]) => void;
}

export interface IUserGroupsAPI {
    groups: string[];
    nextToken?: string;
}

export interface IDeletedGroupAPI {
    groupId: string;
}

interface IGroupOptions {
    groupName: string; // this is the group name as it exists on the backend
    friendlyName: string;
    description: string;
}

interface CustomOption extends ComboboxOption {
    value: string; // this will need to be the friendly name, as it is displayed once selected.
    label: string;
    subtitle?: string;
}

const CustomCombobox = (
    props: ComboboxProps<CustomOption, ComboboxGroup<CustomOption>>,
) => <Combobox {...props} />;

// TODO: rename to proper term - "report permissions" or so (this is not user roles management)
export const UserRoles: React.FunctionComponent<UserRolesProps> = ({
    initialQsGroups,
    fixedQsGroups,
    disabledDisplayText,
    disabledInfoText,
    onRolesChange,
}) => {
    const { includeIntegrationTestGroup } = useAppConfigContext();
    const { t } = useTranslation();
    const classes = useStyles();
    const getHelpLink = useHelpLinks();

    const [error] = useState<string | undefined>();
    const [selectedQsGroups, setSelectedQsGroups] = useState<string[]>([]);

    // This is a temporary state that is updated when the user adds/removes values in the Combobox
    const [tempAssignedGroups, setTempAssignedGroups] = useState<string[]>([]);

    const testOnlyGroups = useMemo(
        () => (includeIntegrationTestGroup ? ['IntegrationUITestGroup'] : []),
        [includeIntegrationTestGroup],
    );

    // Hardcoded group options for now. The 'GroupName' is the group name as it exists on the backend.
    const groupNames = useMemo(() => {
        const baseGroups = [
            'ReportViewer.LEADING',
            'ReportViewer.LEARNING',
            'ReportViewer.TEACHING',
            'BbDataReportViewer',
            'BbDataRestrictedViewer',
            'BbDataUnrestricted',
        ];

        return [...baseGroups, ...testOnlyGroups];
    }, [testOnlyGroups]);

    // TODO: figure out how to get the roleOptionsFriendlyNames translatable in the Combobox once
    // they have been selected
    const groupOptions: IGroupOptions[] = useMemo(
        () =>
            groupNames.map(groupName => {
                if (testOnlyGroups.includes(groupName)) {
                    return {
                        groupName,
                        friendlyName: groupName,
                        description: 'A group used for integration testing',
                    };
                }
                const baseKey = camelCase(groupName.replace(/\./g, ' '));
                const friendlyNameKey = `settings.editUser.roles.${baseKey}.displayName`;
                const descriptionKey = `settings.editUser.roles.${baseKey}.description`;

                return {
                    groupName,
                    friendlyName: t(friendlyNameKey),
                    description: t(descriptionKey),
                };
            }),
        [groupNames, testOnlyGroups, t],
    );

    // on first load, select the initial groups
    useEffect(() => {
        setSelectedQsGroups(initialQsGroups);
    }, [initialQsGroups]);

    useEffect(() => {
        setTempAssignedGroups([]);
    }, [selectedQsGroups]);

    const disableQsGroupSelection = !!fixedQsGroups;
    // Admin: fixedQsGroups=['BbDataReportViewer'] → display that; Developer: fixedQsGroups=[] → display selectedQsGroups (also [])
    const qsGoupsToDisplay = disableQsGroupSelection
        ? fixedQsGroups || []
        : selectedQsGroups;

    const getGroupNameFromFriendlyName = (
        friendlyName: string,
        options: IGroupOptions[],
    ): string | undefined => {
        const mapping = options.find(
            group => group.friendlyName === friendlyName,
        );
        return mapping ? mapping.groupName : undefined;
    };

    const addGroups = (
        friendlyGroupNames: string[],
        options: IGroupOptions[],
    ) => {
        const groupNames = friendlyGroupNames
            .map(
                friendlyName =>
                    getGroupNameFromFriendlyName(friendlyName, options) || '',
            )
            .filter(group => group !== '');

        // remove the groups that are already assigned
        const filteredGroups = groupNames.filter(
            group => !selectedQsGroups.includes(group),
        );

        const newGroups = [
            ...new Set([...filteredGroups, ...selectedQsGroups]),
        ];
        setSelectedQsGroups(newGroups);
        onRolesChange(newGroups);
    };

    const removeGroup = (groupId: string) => {
        const filtered = selectedQsGroups.filter(item => item !== groupId);

        setSelectedQsGroups([...filtered]);
        onRolesChange([...filtered]);
    };

    const convertToHyphenatedString = (input: string): string =>
        input
            .replace(/\./g, '-') // Replace dots with hyphens
            .replace(/\s/g, '-') // Replace spaces with hyphens
            .toLowerCase();

    const getCheckMark = (id: string, groups: string[]) => {
        const check = groups.find(group => group === id);
        if (check) {
            return (
                <Check
                    data-testid={`check-mark-${convertToHyphenatedString(id)}`}
                />
            );
        }
        return null;
    };

    // These provided the text strings for the Combobox
    const comboBoxTextValues = {
        announceOptionSelected: (option: any) =>
            t('settings.editUser.comboBox.optionSelected', {
                option: option.label,
            }),
        announceOptionDeselected: (option: any) =>
            t('settings.editUser.comboBox.optionDeselected', {
                option: option.label,
            }),
        announceValueCleared: t('settings.editUser.comboBox.valueCleared'),
        announceSearchResults: (count: any) => {
            switch (count) {
                case 0:
                    return t('settings.editUser.comboBox.noResultsFound');
                default:
                    return t('settings.editUser.comboBox.resultsFound', {
                        count,
                    });
            }
        },
        noResults: () => t('settings.editUser.comboBox.noResultsFound'),
        clearButtonLabel: t('settings.editUser.comboBox.clearButton'),
        searchLabel: t('settings.editUser.comboBox.searchLabel'),
    };

    const comboBoxGroupOptions = groupOptions.map(group => ({
        value: group.friendlyName, // Keep the value as the friendly name for the Combobox, so the user never sees the real one.
        label: group.friendlyName,
        subtitle: group.description,
    }));

    const selected = tempAssignedGroups.map(group => ({
        value: group,
        label: group,
    }));

    const getFriendlyNameFromGroupName = (
        groupName: string,
        options: IGroupOptions[],
    ): string | undefined => {
        const mapping = options.find(group => group.groupName === groupName);
        return mapping ? mapping.friendlyName : undefined;
    };

    const renderError = () => <EditUserError error={error ?? ''} />;

    const onComboBoxRowKeyDown = (event: any, id: string) => {
        if (event.key === 'Enter') {
            if (tempAssignedGroups.includes(id)) {
                setTempAssignedGroups(
                    tempAssignedGroups.filter(group => group !== id),
                );
            } else {
                setTempAssignedGroups(prevTempAssignedGroup => [
                    ...prevTempAssignedGroup,
                    id,
                ]);
            }
        }
    };

    const renderComboBoxRows = (
        menuItemProps: React.PropsWithChildren<MenuItemProps<CustomOption>>,
    ) => (
        <div
            tabIndex={0}
            data-testid={`role-menu-container-${convertToHyphenatedString(
                menuItemProps.option.value,
            )}`}
            className={classes.userRolesMenuContainer}
            onKeyDown={event =>
                onComboBoxRowKeyDown(event, menuItemProps.option.value)
            }
        >
            <MenuItem
                data-testid={`role-menu-item-${convertToHyphenatedString(
                    menuItemProps.option.value,
                )}`}
                className={classes.userRolesMenuItem}
                {...menuItemProps}
            >
                <div
                    data-testid={`check-box-${convertToHyphenatedString(
                        menuItemProps.option.value,
                    )}`}
                    className={classes.checkBoxContainer}
                >
                    {getCheckMark(
                        menuItemProps.option.value,
                        tempAssignedGroups,
                    )}
                </div>
                <Box
                    data-testid={`role-description-container-${convertToHyphenatedString(
                        menuItemProps.option.value,
                    )}`}
                    component="span"
                    mr="auto"
                    className={classes.userRolesEntry}
                >
                    <div
                        data-testid={`role-label-${convertToHyphenatedString(
                            menuItemProps.option.value,
                        )}`}
                        className={classes.userRolesLabel}
                    >
                        {menuItemProps.option.label}
                    </div>
                    <div
                        data-testid={`role-description-${convertToHyphenatedString(
                            menuItemProps.option.value,
                        )}`}
                        className={classes.userRoleDescription}
                    >
                        {menuItemProps.option.subtitle}
                    </div>
                </Box>
            </MenuItem>
        </div>
    );

    const renderTrashButton = (group: string, disabled: boolean) => {
        const friendlyName =
            getFriendlyNameFromGroupName(group, groupOptions) ?? group;
        return (
            <IconButton
                aria-label={t('settings.editUser.roles.removeRoleButton', {
                    roleName: friendlyName,
                })}
                className={classes.removeRoleButton}
                onClick={() => {
                    removeGroup(group);
                }}
                data-testid={`remove-role-button-${convertToHyphenatedString(
                    group,
                )}`}
                disabled={disabled}
            >
                <Trash />
            </IconButton>
        );
    };

    return (
        <ExpansionPanel
            defaultExpanded={true}
            className={classes.reportPermsSection}
        >
            <ExpansionPanelSummary
                id="report-permissions-assigned-summary"
                aria-controls="report-permissions-assigned-details"
            >
                <div>
                    <Typography variant="h2">
                        {t('settings.editUser.roles.title')}
                        {/* prettier-ignore */}
                    </Typography>
                    <Typography variant="body1">
                        {t('settings.editUser.roles.description')}
                    </Typography>
                </div>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
                <Box
                    data-testid="user-roles-combo-box-container"
                    className={`${classes.comboBoxContainer}`}
                >
                    {error && renderError()}
                    {!error && (
                        <>
                            <Box className={classes.box}>
                                <CustomCombobox
                                    id="user-roles-combo-box"
                                    data-testid="user-roles-combo-box"
                                    closeOnSelect={false}
                                    isMulti
                                    strings={comboBoxTextValues}
                                    label={t(
                                        'settings.editUser.roles.comboBox.mainLabel',
                                    )}
                                    placeholder={t(
                                        'settings.editUser.roles.comboBox.multiSelectPlaceHolder',
                                    )}
                                    isClearable={true}
                                    options={comboBoxGroupOptions}
                                    defaultValue={selected}
                                    className={classes.userRolesComboBox}
                                    onChange={value => {
                                        const updatedSelection = value.map(
                                            option => option.value,
                                        );
                                        setTempAssignedGroups(updatedSelection);
                                    }}
                                    components={{
                                        MenuItem: props =>
                                            renderComboBoxRows(props),
                                    }}
                                    disabled={disableQsGroupSelection}
                                />
                                <OutlineButton
                                    data-testid="add-role-button"
                                    disabled={!tempAssignedGroups.length}
                                    onClick={() => {
                                        addGroups(
                                            tempAssignedGroups,
                                            groupOptions,
                                        );
                                    }}
                                    className={classes.addRoleButton}
                                >
                                    {t('settings.editUser.roles.addRoleButton')}
                                </OutlineButton>
                            </Box>
                            {disableQsGroupSelection ? (
                                disabledInfoText && (
                                    <Typography data-testid="disabled-perms-info">
                                        <Information
                                            className={classes.infoIcon}
                                        />
                                        {disabledInfoText}
                                    </Typography>
                                )
                            ) : (
                                <Typography data-testid="role-information">
                                    <Information className={classes.infoIcon} />
                                    {/* TODO: Add message about needing to wait awhile for the user to have updated roles after submission */}
                                    {t(
                                        'settings.editUser.roles.addRoleInformation',
                                    )}{' '}
                                    <Link href={getHelpLink('roles')}>
                                        {t(
                                            'settings.editUser.roles.addRoleSeeHow',
                                        )}
                                    </Link>
                                </Typography>
                            )}
                            <List data-testid="assigned-roles-list">
                                {disableQsGroupSelection &&
                                    qsGoupsToDisplay.length === 0 &&
                                    disabledDisplayText && (
                                        <ListItem
                                            data-testid="assigned-role-list-item-disabled-text"
                                            className={classes.assignedRoleRow}
                                        >
                                            <ListItemText
                                                data-testid="assigned-role-name-disabled-text"
                                                className={
                                                    classes.assignedRoleRowText
                                                }
                                            >
                                                {disabledDisplayText}
                                            </ListItemText>
                                        </ListItem>
                                    )}
                                {qsGoupsToDisplay.map(group => (
                                    <ListItem
                                        key={group}
                                        data-testid={`assigned-role-list-item-${convertToHyphenatedString(
                                            group,
                                        )}`}
                                        className={classes.assignedRoleRow}
                                    >
                                        <ListItemText
                                            data-testid={`assigned-role-name-${convertToHyphenatedString(
                                                group,
                                            )}`}
                                            className={
                                                classes.assignedRoleRowText
                                            }
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                            }}
                                        >
                                            <span>
                                                {getFriendlyNameFromGroupName(
                                                    group,
                                                    groupOptions,
                                                )}
                                            </span>
                                            {renderTrashButton(
                                                group,
                                                disableQsGroupSelection,
                                            )}
                                        </ListItemText>
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                </Box>
            </ExpansionPanelDetails>
        </ExpansionPanel>
    );
};
